import React, { useState, useEffect } from 'react';
import './Auth.css';

const Auth = ({
  screen,
  goRegister,
  goLogin,
  goCode,
  goForgot,
  goReset,
  enterApp,
  onRegister,
  onVerify,
  onLogin,
  onForgotPassword,
  onResetPassword,
  resendTimer,
  onResend,
  resending,
  forgotEmail,
  twofaUserId,
  twofaCode,
  setTwofaCode,
  twofaError,
  setTwofaError,
  onVerify2fa,
  onGoLogin,
  resetResendTimer = 0,
  onResetResend,
  resetResending = false,
  ref: initialRef,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState(forgotEmail || '');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const [showPassLogin, setShowPassLogin] = useState(false);
  const [showPassRegister, setShowPassRegister] = useState(false);
  const [showPassReset, setShowPassReset] = useState(false);

  const [modal, setModal] = useState({ visible: false, title: '', message: '', onClose: null });

  const showModal = (title, message, onClose = null) => {
    setModal({ visible: true, title, message, onClose });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, visible: false }));
    if (modal.onClose) modal.onClose();
  };

  useEffect(() => {
    setError('');
    setLoading(false);

    setPassword('');
    setPasswordConfirm('');
    setCode('');
    setResetCode('');
    setNewPassword('');
    setNewPasswordConfirm('');

    setShowPassLogin(false);
    setShowPassRegister(false);
    setShowPassReset(false);

    switch (screen) {
      case 'login':
      case 'register':
        setEmail('');           
        break;
      case 'forgot':
        setResetEmail(forgotEmail || '');
        break;
      case 'reset':
        break;
      case 'code':
        break;
      case 'twofa':
        break;
      default:
        break;
    }
  }, [screen, forgotEmail]);

  const formatCode = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    return digits.split('').join(' ');
  };

  const sanitizePassword = (val) => {
    return val.replace(/[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/g, '');
  };

  const handleRegister = async () => {
    if (password !== passwordConfirm) {
      setError('Пароли не совпадают');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onRegister(email, password);
      goCode(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError('');
    try {
      const tokenData = await onVerify(email, code, initialRef);
      enterApp(tokenData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await onLogin(email, password);
      if (result === null) {
        setLoading(false);
        return;
      }
      enterApp(result);
    } catch (err) {
      setError(err.message);
    } finally {
      if (screen !== 'twofa') setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!resetEmail) {
      setError('Введите email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onForgotPassword(resetEmail);
      goReset(resetEmail);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (newPassword !== newPasswordConfirm) {
      setError('Пароли не совпадают');
      return;
    }
    if (newPassword.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onResetPassword(resetEmail, resetCode, newPassword);
      showModal(
        'Пароль изменён',
        'Теперь вы можете войти с новым паролем.',
        goLogin
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async () => {
    if (!twofaCode || twofaCode.length !== 6) {
      setTwofaError('Введите 6-значный код');
      return;
    }
    setLoading(true);
    setTwofaError('');
    try {
      const tokenData = await onVerify2fa(twofaUserId, twofaCode);
      enterApp(tokenData);
    } catch (err) {
      setTwofaError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderLogin = () => {
    const isFormValid = email.trim() !== '' && password.trim() !== '';
    return (
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo-icon">
            <svg width="22" height="22" viewBox="0 0 24 24">
              <defs>
                <linearGradient id="riseBolt" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#34d399" />
                  <stop offset="1" stopColor="#0f8f5f" />
                </linearGradient>
              </defs>
              <path d="M13 2 L5 13 h5 l-1.5 9 L19 10 h-5.5 z" fill="url(#riseBolt)" />
            </svg>
          </div>
          <span className="auth-brand">Rise<span className="auth-brand-accent">VPN</span></span>
        </div>
        <h1 className="auth-title">Вход</h1>
        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="3" y="5" width="18" height="14" rx="2.5" />
            <path d="M4 7l8 6 8-6" />
          </svg>
          <input
            type="email"
            placeholder="Email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="4" y="10" width="16" height="10" rx="2.5" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <input
            type={showPassLogin ? 'text' : 'password'}
            placeholder="Пароль"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(sanitizePassword(e.target.value))}
          />
          <button
            type="button"
            className="auth-toggle-password"
            onClick={() => setShowPassLogin(!showPassLogin)}
          >
            {showPassLogin ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </label>
        {error && <div className="auth-error" style={{ color: '#ff6b6b', marginBottom: '12px', fontSize: '14px' }}>{error}</div>}
        <button onClick={handleLogin} className="auth-button" disabled={loading || !isFormValid}>
          {loading ? 'Загрузка...' : 'Войти →'}
        </button>
        <div className="auth-links">
          <a href="#" onClick={goForgot}>Забыли пароль?</a>
        </div>
        <div className="auth-links">
          Нет аккаунта? <a href="#" onClick={goRegister}>Зарегистрироваться</a>
        </div>
      </div>
    );
  };

  const renderRegister = () => {
    const isFormValid = email.trim() !== '' && password.trim() !== '' && passwordConfirm.trim() !== '';
    return (
      <div className="auth-card">
        <h1 className="auth-title">Регистрация</h1>
        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="3" y="5" width="18" height="14" rx="2.5" />
            <path d="M4 7l8 6 8-6" />
          </svg>
          <input
            type="email"
            placeholder="Email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="4" y="10" width="16" height="10" rx="2.5" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <input
            type={showPassRegister ? 'text' : 'password'}
            placeholder="Пароль"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(sanitizePassword(e.target.value))}
          />
          <button
            type="button"
            className="auth-toggle-password"
            onClick={() => setShowPassRegister(!showPassRegister)}
          >
            {showPassRegister ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </label>
        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="4" y="10" width="16" height="10" rx="2.5" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <input
            type={showPassRegister ? 'text' : 'password'}
            placeholder="Повторите пароль"
            className="auth-input"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(sanitizePassword(e.target.value))}
          />
          <button
            type="button"
            className="auth-toggle-password"
            onClick={() => setShowPassRegister(!showPassRegister)}
          >
            {showPassRegister ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </label>
        {error && <div className="auth-error" style={{ color: '#ff6b6b', marginBottom: '12px', fontSize: '14px' }}>{error}</div>}
        <button onClick={handleRegister} className="auth-button" disabled={loading || !isFormValid}>
          {loading ? 'Загрузка...' : 'Получить код →'}
        </button>
        <div className="auth-links">
          Уже есть аккаунт? <a href="#" onClick={goLogin}>Войти</a>
        </div>
      </div>
    );
  };

  const renderCode = () => {
    const isFormValid = code.length === 6;
    return (
      <div className="auth-card code-card">
        <h1 className="auth-title">Код подтверждения</h1>
        <p className="auth-code-info">Отправлен на <b>{email}</b></p>
        <p className="auth-code-hint">Если письма нет — проверьте email на опечатки и папку «Спам».</p>
        
        <label className="auth-code-input-group">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <circle cx="8" cy="12" r="4" />
            <path d="M12 12h9M18 12v4M15 12v3" />
          </svg>
          <input
            inputMode="numeric"
            maxLength="11"
            placeholder="0 0 0 0 0 0"
            className="auth-code-input"
            value={formatCode(code)}
            onChange={(e) => {
              const formatted = formatCode(e.target.value);
              setCode(formatted.replace(/\s/g, ''));
            }}
          />
        </label>

        {error && <div className="auth-error" style={{ color: '#ff6b6b', marginBottom: '12px', fontSize: '14px' }}>{error}</div>}
        
        <button onClick={handleVerify} className="auth-button" disabled={loading || !isFormValid}>
          {loading ? 'Загрузка...' : 'Создать аккаунт →'}
        </button>

        <div className="auth-code-footer">
          <a href="#" onClick={goRegister} style={{ color: '#c8d0d9' }}>← Назад</a>
          <button
            onClick={onResend}
            disabled={resendTimer > 0 || resending}
            style={{
              background: 'none',
              border: 'none',
              color: (resendTimer > 0 || resending) ? '#65707d' : '#4aae88',
              cursor: (resendTimer > 0 || resending) ? 'default' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              textDecoration: 'underline',
            }}
          >
            {resending ? 'Отправка...' : resendTimer > 0 ? `Отправить снова (${resendTimer}с)` : 'Отправить снова'}
          </button>
        </div>
      </div>
    );
  };

  const renderForgot = () => {
    const isFormValid = resetEmail.trim() !== '';
    return (
      <div className="auth-card">
        <h1 className="auth-title">Восстановление пароля</h1>
        <p className="auth-code-info">Введите ваш email, мы пришлём код для сброса.</p>
        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="3" y="5" width="18" height="14" rx="2.5" />
            <path d="M4 7l8 6 8-6" />
          </svg>
          <input
            type="email"
            placeholder="Email"
            className="auth-input"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
        </label>
        {error && <div className="auth-error" style={{ color: '#ff6b6b', marginBottom: '12px', fontSize: '14px' }}>{error}</div>}
        <button onClick={handleForgot} className="auth-button" disabled={loading || !isFormValid}>
          {loading ? 'Загрузка...' : 'Отправить код →'}
        </button>
        <div className="auth-links">
          <a href="#" onClick={goLogin}>← Вернуться ко входу</a>
        </div>
      </div>
    );
  };

  const renderReset = () => {
    const isFormValid = resetCode.length === 6 && newPassword.trim() !== '' && newPasswordConfirm.trim() !== '';

    return (
      <div className="auth-card">
        <h1 className="auth-title">Новый пароль</h1>
        <p className="auth-code-info">Введите код из письма и установите новый пароль.</p>
        
        <label className="auth-code-input-group">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <circle cx="8" cy="12" r="4" />
            <path d="M12 12h9M18 12v4M15 12v3" />
          </svg>
          <input
            inputMode="numeric"
            maxLength="11"
            placeholder="0 0 0 0 0 0"
            className="auth-code-input"
            value={formatCode(resetCode)}
            onChange={(e) => {
              const formatted = formatCode(e.target.value);
              setResetCode(formatted.replace(/\s/g, ''));
            }}
          />
        </label>

        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="4" y="10" width="16" height="10" rx="2.5" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <input
            type={showPassReset ? 'text' : 'password'}
            placeholder="Новый пароль"
            className="auth-input"
            value={newPassword}
            onChange={(e) => setNewPassword(sanitizePassword(e.target.value))}
          />
          <button
            type="button"
            className="auth-toggle-password"
            onClick={() => setShowPassReset(!showPassReset)}
          >
            {showPassReset ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </label>

        <label className="auth-input-group">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="4" y="10" width="16" height="10" rx="2.5" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <input
            type={showPassReset ? 'text' : 'password'}   
            placeholder="Подтвердите пароль"
            className="auth-input"
            value={newPasswordConfirm}
            onChange={(e) => setNewPasswordConfirm(sanitizePassword(e.target.value))}
          />
          <button
            type="button"
            className="auth-toggle-password"
            onClick={() => setShowPassReset(!showPassReset)}
          >
            {showPassReset ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </label>

        {error && <div className="auth-error" style={{ color: '#ff6b6b', marginBottom: '12px', fontSize: '14px' }}>{error}</div>}
        
        <button onClick={handleReset} className="auth-button" disabled={loading || !isFormValid}>
          {loading ? 'Загрузка...' : 'Сбросить пароль →'}
        </button>

        <div className="auth-code-footer">
          <a href="#" onClick={goLogin} style={{ color: '#c8d0d9' }}>← Вернуться ко входу</a>
          
          {onResetResend && (
            <button
              onClick={onResetResend}
              disabled={resetResendTimer > 0 || resetResending}
              style={{
                background: 'none',
                border: 'none',
                color: (resetResendTimer > 0 || resetResending) ? '#65707d' : '#4aae88',
                cursor: (resetResendTimer > 0 || resetResending) ? 'default' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                textDecoration: 'underline',
              }}
            >
              {resetResending 
                ? 'Отправка...' 
                : resetResendTimer > 0 
                  ? `Отправить снова (${resetResendTimer}с)` 
                  : 'Отправить код снова'
              }
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTwofa = () => {
    const isFormValid = twofaCode && twofaCode.length === 6;
    return (
      <div className="auth-card code-card">
        <h1 className="auth-title">Двухфакторная аутентификация</h1>
        <p className="auth-code-info">Введите код, отправленный на вашу почту.</p>
        
        <label className="auth-code-input-group">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <circle cx="8" cy="12" r="4" />
            <path d="M12 12h9M18 12v4M15 12v3" />
          </svg>
          <input
            inputMode="numeric"
            maxLength="11"
            placeholder="0 0 0 0 0 0"
            className="auth-code-input"
            value={formatCode(twofaCode || '')}
            onChange={(e) => {
              const formatted = formatCode(e.target.value);
              setTwofaCode(formatted.replace(/\s/g, ''));
              setTwofaError('');
            }}
          />
        </label>

        {twofaError && <div className="auth-error" style={{ color: '#ff6b6b', marginBottom: '12px', fontSize: '14px' }}>{twofaError}</div>}
        
        <button onClick={handleVerify2fa} className="auth-button" disabled={loading || !isFormValid}>
          {loading ? 'Загрузка...' : 'Подтвердить →'}
        </button>

        <div className="auth-code-footer">
          <a href="#" onClick={onGoLogin} style={{ color: '#c8d0d9' }}>← Назад ко входу</a>
          <button
            onClick={() => {
              showModal(
                'Новый код отправлен',
                'Пожалуйста, введите пароль заново на странице входа, чтобы получить новый код.'
              );
              onGoLogin();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#4aae88',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              textDecoration: 'underline',
            }}
          >
            Отправить код снова
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="auth-wrap">
      <div className="auth-backdrop"></div>
      <div className="auth-letter">R</div>
      <div className="deco-side left">SECURE&nbsp;TUNNEL<br />[ ONLINE ]</div>
      <div className="deco-side right">
        SYS: RISE-CORE<br />NODE: NL-01<br />PING: 12ms<br />STATUS: SECURE
      </div>

      {screen === 'login' && renderLogin()}
      {screen === 'register' && renderRegister()}
      {screen === 'code' && renderCode()}
      {screen === 'forgot' && renderForgot()}
      {screen === 'reset' && renderReset()}
      {screen === 'twofa' && renderTwofa()}
      {modal.visible && (
        <div className="auth-modal-overlay" onClick={closeModal}>
          <div className="auth-modal" onClick={e => e.stopPropagation()}>
            <div className="auth-modal-header">
              <h3>{modal.title}</h3>
            </div>
            <div className="auth-modal-body">
              <p>{modal.message}</p>
            </div>
            <div className="auth-modal-footer">
              <button onClick={closeModal} className="auth-button">
                Хорошо
              </button>
            </div>
          </div>
        </div>  
      )}
    </div>
  );
};

export default Auth;